import { useState, useEffect } from 'react';
import DateRangePicker from './DateRangePicker';
import { supabase } from '../lib/supabase';
import { fmt$ } from '../utils/formatters';
import { getRange, fmtDateShort, PRESETS } from '../utils/dateRange';
import InspectName from './InspectName';

const CAT_ICONS = { price: '💲', invoice: '📄', team: '👥', menu: '🍽', batch: '🧑‍🍳', settings: '⚙' };
const CAT_COLORS = (t) => ({ price: t.yl, invoice: t.bl, team: t.gn, menu: t.ac, batch: t.tm, settings: t.td });

export default function ActivityLog({ t, si, sb, sh, sd, mn, orgId }) {
const [entries, setEntries] = useState([]);
const [loading, setLoading] = useState(true);
const [preset, setPreset] = useState('this-month');
const [customStart, setCustomStart] = useState('');
const [customEnd, setCustomEnd] = useState('');
const [filterCat, setFilterCat] = useState('All');
const range = getRange(preset, { start: customStart, end: customEnd });

useEffect(() => {
  if (!orgId) return;
  loadLog();
}, [orgId, range.start, range.end]);

async function loadLog() {
  setLoading(true);
  const { data } = await supabase.rpc('get_activity_log', { p_org_id: orgId, p_limit: 500, p_offset: 0 });
  if (data) {
    const filtered = data.filter(e => {
      const d = e.created_at.split('T')[0];
      return d >= range.start && d <= range.end;
    });
    setEntries(filtered);
  }
  setLoading(false);
}

const filtered = filterCat === 'All' ? entries : entries.filter(e => e.category === filterCat);
const colors = CAT_COLORS(t);

// Group by date
const byDate = {};
filtered.forEach(e => { const d = e.created_at.split('T')[0]; if (!byDate[d]) byDate[d] = []; byDate[d].push(e); });
const dateKeys = Object.keys(byDate).sort().reverse();

return (<div>
<h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 20px" }}>Activity Log</h1>

{/* Date range */}
<div style={{ background: t.cd, borderRadius: 12, border: `1px solid ${t.bd}`, padding: "14px 18px", marginBottom: 16 }}>
<div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
{PRESETS.map(p => <button key={p.id} onClick={() => setPreset(p.id)} style={{ ...sb, background: preset === p.id ? t.bl : t.sf, color: preset === p.id ? "#fff" : t.tm, border: `1px solid ${preset === p.id ? t.bl : t.bd}`, padding: "6px 12px", fontSize: 10 }}>{p.label}</button>)}
</div>
{preset === 'custom' && <DateRangePicker start={customStart} end={customEnd} onChange={(s,e)=>{setCustomStart(s);setCustomEnd(e);}} t={t} si={si}/>}
<div style={{ fontSize: 11, color: t.tm, marginTop: 8 }}>{fmtDateShort(range.start)} — {fmtDateShort(range.end)}</div>
</div>

{/* Category filter */}
<div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
{['All', 'price', 'invoice', 'team', 'menu', 'batch', 'settings'].map(c =>
<button key={c} onClick={() => setFilterCat(c)} style={{ ...sb, background: filterCat === c ? t.bl : t.sf, color: filterCat === c ? "#fff" : t.tm, border: `1px solid ${filterCat === c ? t.bl : t.bd}`, padding: "6px 12px", fontSize: 10 }}>
{c === 'All' ? 'All' : (CAT_ICONS[c] || '') + ' ' + c.charAt(0).toUpperCase() + c.slice(1)}
</button>)}
<span style={{ marginLeft: "auto", fontSize: 11, color: t.tm }}>{filtered.length} entries</span>
</div>

{/* Activity entries */}
{loading ? <div style={{ color: t.td, padding: 20 }}>Loading activity...</div> :
filtered.length === 0 ? <div style={{ background: t.cd, borderRadius: 12, border: `1px solid ${t.bd}`, padding: 20, textAlign: "center", color: t.td }}>No activity in this period</div> :
dateKeys.map(dk => (
<div key={dk} style={{ marginBottom: 16 }}>
<div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: t.tm }}>{fmtDateShort(dk)}</div>
{byDate[dk].map(e => {
  const time = new Date(e.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const color = colors[e.category] || t.tm;
  const icon = CAT_ICONS[e.category] || '•';
  return (
    <div key={e.id} style={{ display: "flex", gap: 12, padding: "10px 14px", background: t.cd, border: `1px solid ${t.bd}`, borderRadius: 8, marginBottom: 6 }}>
      <div style={{ fontSize: 18, width: 28, textAlign: "center", flexShrink: 0, paddingTop: 2 }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{e.summary}</div>
        <div style={{ display: "flex", gap: 12, fontSize: 10, color: t.td }}>
          <span>{time}</span>
          <span>{e.user_email}</span>
          <span style={{ color, fontWeight: 600, textTransform: "uppercase" }}>{e.category}</span>
        </div>
        {/* Price change details */}
        {e.category === 'price' && e.details?.items && <div style={{ marginTop: 6 }}>
          {e.details.items.slice(0, 5).map((item, i) => (
            <div key={i} style={{ fontSize: 11, color: t.tm, display: "flex", gap: 8 }}>
              <span>{item.name}</span>
              <span style={{ fontFamily: mn, color: t.td }}>${fmt$(item.old)}</span>
              <span>→</span>
              <span style={{ fontFamily: mn, color: item.new > item.old ? t.ac : t.gn }}>${fmt$(item.new)}</span>
            </div>))}
          {e.details.items.length > 5 && <div style={{ fontSize: 10, color: t.td }}>+{e.details.items.length - 5} more</div>}
        </div>}
        {/* Invoice details */}
        {e.category === 'invoice' && e.details && <div style={{ fontSize: 11, color: t.tm, marginTop: 4 }}>
          <InspectName type="vendor" item={e.details.vendor} orgId={orgId} t={t}>{e.details.vendor}</InspectName> {e.details.invoiceNumber && `#${e.details.invoiceNumber}`} — {e.details.itemCount} items
        </div>}
      </div>
    </div>
  );
})}
</div>))}
</div>);
}
